import { Dispatch } from "redux";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { updateVerificationServiceChangeRequestRequest } from "@requests/service-configuration";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import { resources } from "@constants/hardcoded-strings";
import { ModalContextData } from "@idw-types/context";
import { ModalTypes } from "@enums/modal-types";
import { VerificationServiceChangeRequest } from "@idw-types/data/verification-service";
import { CreateVerificationServiceChangeRequestModal } from "@containers/modals/create-verification-service-change-request-modal/create-verification-service-change-request-modal";
import { SubmitVerificationServiceChangeRequestModal } from "@containers/modals/submit-verification-service-change-request-modal/submit-verification-service-change-request-modal";
import { CancelVerificationServiceChangeRequestModal } from "@containers/modals/cancel-verification-service-change-request-modal/cancel-verification-service-change-request-modal";
import { UnsubmitVerificationServiceChangeRequestModal } from "@containers/modals/unsubmit-verification-service-change-request-modal/unsubmit-verification-service-change-request-modal";
import { DeployNowVerificationServiceChangeRequestModal } from "@containers/modals/deploy-now-verification-service-change-request-modal/deploy-now-verification-service-change-request-modal";
import { RejectVerificationServiceChangeRequestModal } from "@containers/modals/reject-verification-service-change-request-modal/reject-verification-service-change-request-modal";
import { ApproveVerificationServiceChangeRequestModal } from "@containers/modals/approve-verification-service-change-request-modal/approve-verification-service-change-request-modal";

export const performVerificationServiceChangeRequestStep = async (
    dispatch: Dispatch,
    rpServiceId: string,
    changeRequestStatus: ChangeRequestStatusTypes,
    body: { [key: string]: any }
) => {
    const { data, error } = await updateVerificationServiceChangeRequestRequest(rpServiceId, body, changeRequestStatus);

    if (data?.transferObject) {
        return data.transferObject;
    } else if (error) {
        logAndDisplayErrorModal(
            dispatch,
            error,
            resources.requests.updateVerificationServiceChangeRequest.error.title,
            resources.requests.updateVerificationServiceChangeRequest.error.message,
            error,
            null,
            body
        );
    }
    return null;
};

export const deployVerificationService = async (
    dispatch: Dispatch,
    rpServiceId: string,
    changeRequestStatuses: Array<ChangeRequestStatusTypes>,
    currentStepIndex: number
): Promise<any> => {
    const currentChangeRequestStatus = changeRequestStatuses[currentStepIndex];

    if (currentStepIndex < changeRequestStatuses.length) {
        const body = changeRequestStatuses[currentStepIndex] === ChangeRequestStatusTypes.DEPLOYED
            ? { deploymentDate: new Date().getTime() }
            : null;

        const response = await performVerificationServiceChangeRequestStep(
            dispatch,
            rpServiceId,
            currentChangeRequestStatus,
            body
        );

        if (response && changeRequestStatuses[currentStepIndex] !== ChangeRequestStatusTypes.DEPLOYED) {
            return await deployVerificationService(dispatch, rpServiceId, changeRequestStatuses, currentStepIndex + 1);
        } else {
            return response;
        }
    }

    return null;
};


export const deployVerificationServiceInOrder = async (
    dispatch: Dispatch,
    rpServiceId: string,
    changeRequestStatuses: Array<ChangeRequestStatusTypes>
): Promise<VerificationServiceChangeRequest & { done: boolean }> => {
    let currentStepIndex = 0;
    let response;
    let hasError = false;

    while (!hasError && currentStepIndex < changeRequestStatuses.length) {
        const body = changeRequestStatuses[currentStepIndex] === ChangeRequestStatusTypes.DEPLOYED
            ? { deploymentDate: new Date().getTime() }
            : null;

        const currentChangeRequestStatus = changeRequestStatuses[currentStepIndex];

        response = await performVerificationServiceChangeRequestStep(
            dispatch,
            rpServiceId,
            currentChangeRequestStatus,
            body
        );

        if (!response) {
            hasError = true;
        }

        currentStepIndex++;
    }

    return { ...response, done: !hasError };
};


export const clickVerificationServiceChangeRequestActionHandler = (
    dispatch: Dispatch,
    onSubmitHandler: (onModalClose: () => void, data?: any) => Promise<VerificationServiceChangeRequest>,
    modalContext: ModalContextData,
    rpServiceId: string,
    serviceName: string,
    apiKey: string,
    modalType: ModalTypes
) => {
    if (rpServiceId) {
        const renderModal = (onModalClose: () => void) => {
            switch (modalType) {
                case ModalTypes.CreateVerificationServiceChangeRequest:
                    return <CreateVerificationServiceChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        serviceName={serviceName}
                        apiKey={apiKey}
                    />;
                case ModalTypes.SubmitVerificationServiceChangeRequest:
                    return <SubmitVerificationServiceChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        serviceName={serviceName}
                        apiKey={apiKey}
                    />;
                case ModalTypes.UnsubmitVerificationServiceChangeRequest:
                    return <UnsubmitVerificationServiceChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        serviceName={serviceName}
                        apiKey={apiKey}
                    />;
                case ModalTypes.ApproveVerificationServiceChangeRequest:
                    return <ApproveVerificationServiceChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                    />;
                case ModalTypes.RejectVerificationServiceChangeRequest:
                    return <RejectVerificationServiceChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                    />
                case ModalTypes.CancelVerificationServiceChangeRequest:
                    return <CancelVerificationServiceChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        serviceName={serviceName}
                        apiKey={apiKey}
                    />;
                case ModalTypes.DeployNowVerificationServiceChangeRequest:
                    return <DeployNowVerificationServiceChangeRequestModal
                        onModalClose={onModalClose}
                        onSubmitHandler={onSubmitHandler}
                        serviceName={serviceName}
                        apiKey={apiKey}
                    />;
                default:
                    return null;
            }
        }

        modalContext.setModalState({
            modalType: modalType,
            renderFunction: renderModal
        });
    } else {
        logAndDisplayErrorModal(
            dispatch,
            null,
            resources.pages.verificationServices.errors.invalidConfiguration.title,
            resources.pages.verificationServices.errors.invalidConfiguration.message,
        );
    }
};